// Design rounds: the list on a project page, and the round viewer (overview, screens, notes).
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post } from '../api';
import { useAuth } from '../auth';
import { useLiveTopics } from '../live';
import { Modal, ago, formatDateTime, useConfirm, useToast } from '../ui';
import type { CommentList, Job, Project, Round, RoundDetail, RoundTicket, RoundVisibility, Screen } from '../types';
import { Page, useCrumbs } from './Shell';
import { CommentsPanel, roundCommentsApi, useComments, type CommentsApi } from './Comments';
import { IdeasPanel, useNextRound } from './Ideas';
import { ProjectKitBar, RoundKitDialog } from './Kits';

type PanelTab = 'notes' | 'comments' | 'ideas';

const FRAMES = { phone: { w: 390, h: 844 }, desktop: { w: 1280, h: 800 } };
export const VISIBILITY_LABELS: Record<RoundVisibility, string> = { internal: 'Internal', ready: 'Ready for comment', approved: 'Approved' };
const VISIBILITY_BADGE: Record<RoundVisibility, string> = { internal: '', ready: 'info', approved: 'ok' };
const screenUrl = (projectId: string, n: number, id: string) => `/files/projects/${projectId}/v${n}/screens/${encodeURIComponent(id)}.html`;

export const VisibilityBadge = ({ v }: { v: RoundVisibility }) => <span className={`badge ${VISIBILITY_BADGE[v]}`}>{VISIBILITY_LABELS[v]}</span>;

function useDesignTopics() {
  const { me } = useAuth();
  useLiveTopics(me?.is_staff ? 'workspace' : me?.client ? `client:${me.client.id}` : null);
}

// ---------- scaled screen frame ----------

export function Frame({ src, frame, scale, title, lazy }: { src: string; frame: Screen['frame']; scale: number; title: string; lazy?: boolean }) {
  const f = FRAMES[frame] ?? FRAMES.phone;
  return (
    <div className="frame-box" style={{ width: Math.round(f.w * scale), height: Math.round(f.h * scale) }}>
      {/* Screens are static HTML: sandboxed with no permissions at all. */}
      <iframe src={src} title={title} sandbox="" loading={lazy ? 'lazy' : undefined} tabIndex={-1}
              style={{ width: f.w, height: f.h, transform: `scale(${scale})` }} />
    </div>
  );
}

function useFitScale(frame: Screen['frame'] | undefined, max = 1, reserve = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);
  useEffect(() => {
    const el = ref.current;
    if (!el || !frame) return;
    const f = FRAMES[frame] ?? FRAMES.phone;
    // Leave room either side for the previous/next buttons.
    const fit = () => setScale(Math.max(0.2, Math.min(max, (el.clientWidth - 120) / f.w, (el.clientHeight - 24 - reserve) / f.h)));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [frame, max, reserve]);
  return { ref, scale };
}

// ---------- staff controls ----------

function useRoundActions(project: Project, round: Round) {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const refresh = () => { qc.invalidateQueries({ queryKey: ['rounds', project.id] }); qc.invalidateQueries({ queryKey: ['round', project.id] }); };
  const setVisibility = async (v: RoundVisibility) => {
    if (v === 'ready' && !round.shared_at && !(await confirm(`Share ${round.label} with ${project.client.name}?`, {
      body: project.client_visible
        ? `Their people can view it and comment. Once shared, ${round.label} stays exactly as they saw it: changes go into a new round.`
        : `The project itself is hidden from the client, so they won't see it until you make the project visible. Once shared, ${round.label} can't be rebuilt.`,
      ok: 'Share' }))) return;
    if (v === 'internal' && !(await confirm(`Make ${round.label} internal again?`, { body: "The client's people stop seeing it straight away. Anything they wrote stays.", ok: 'Make internal', danger: true }))) return;
    try { await patch(`/api/projects/${project.id}/rounds/${round.number}`, { visibility: v }); refresh(); } catch (e) { toast((e as Error).message, true); }
  };
  const build = useMutation({
    mutationFn: (pdf: boolean) => post<Job>(`/api/projects/${project.id}/rounds/${round.number}/build`, { pdf }),
    onSuccess: () => { refresh(); toast(`Building ${round.label}…`); },
    onError: (e: Error) => toast(e.message, true),
  });
  return { setVisibility, build, dialog };
}

function BuildStatus({ round, onLog }: { round: Round; onLog: (id: string) => void }) {
  const b = round.build;
  if (!b) return null;
  if (b.status === 'queued' || b.status === 'running') return <span className="row small muted" style={{ gap: 6 }}><span className="spin" />{b.status === 'queued' ? 'Waiting to build' : 'Building'}{b.pdf ? ' + PDF' : ''}</span>;
  if (b.status === 'failed') return <button className="btn sm ghost danger" onClick={() => onLog(b.id)}>Build failed: see log</button>;
  return null;
}

function JobLog({ id, onClose }: { id: string; onClose: () => void }) {
  const { data } = useQuery({ queryKey: ['job', id], queryFn: () => get<Job>(`/api/jobs/${id}`) });
  return (
    <Modal title="Build log" onClose={onClose} width={720}>
      {data ? <>
        <p className="muted small" style={{ margin: 0 }}>{data.status === 'ok' ? 'Succeeded' : data.status === 'failed' ? 'Failed' : 'In progress'}{data.finished_at ? ` · ${formatDateTime(data.finished_at)}` : ''}</p>
        <pre className="log">{data.log || '(no output)'}</pre>
      </> : <span className="spin muted" />}
    </Modal>
  );
}

function RoundControls({ project, round, compact }: { project: Project; round: Round; compact?: boolean }) {
  const { can } = useAuth();
  const { setVisibility, build, dialog } = useRoundActions(project, round);
  const [log, setLog] = useState<string | null>(null);
  const [kitting, setKitting] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const busy = round.build?.status === 'queued' || round.build?.status === 'running' || !!round.claude_working;
  if (!can('delivery.edit')) return null;
  return (
    <div className="row wrap" style={{ gap: 8, justifyContent: compact ? 'flex-end' : undefined }}>
      <BuildStatus round={round} onLog={setLog} />
      {!round.shared_at && !busy && (
        <button className={`btn sm ${round.built && !round.stale ? 'ghost' : ''}`} onClick={() => build.mutate(true)} disabled={build.isPending}
                title="Runs tools/build.mjs: screens, board and PDF">{round.built ? (round.stale ? 'Rebuild (out of date)' : 'Rebuild') : 'Build'}</button>
      )}
      {can('designs.publish') && (
        <select className="input" style={{ width: 'auto', minHeight: 30, padding: '3px 8px' }} value={round.visibility}
                onChange={(e) => setVisibility(e.target.value as RoundVisibility)} aria-label={`Visibility of ${round.label}`} disabled={(!round.built || !!round.claude_working) && round.visibility === 'internal'}
                title={round.built ? '' : 'Build the round before sharing it'}>
          {(Object.keys(VISIBILITY_LABELS) as RoundVisibility[]).map((v) => <option key={v} value={v}>{VISIBILITY_LABELS[v]}</option>)}
        </select>
      )}
      {!round.shared_at && !busy && <button className="btn sm ghost" onClick={() => setKitting(true)} title="Use a UI kit version for this round">Kit…</button>}
      {round.discard_blocked !== undefined && (
        <button className="btn sm ghost" onClick={() => setDiscarding(true)} disabled={!!round.discard_blocked}
                title={round.discard_blocked || `Archive ${round.label} (say it came out wrong) and send its ideas again`}>Discard…</button>
      )}
      {log && <JobLog id={log} onClose={() => setLog(null)} />}
      {kitting && <RoundKitDialog project={project} round={round} onClose={() => setKitting(false)} />}
      {discarding && <DiscardDialog project={project} round={round} onClose={() => setDiscarding(false)} />}
      {dialog}
    </div>
  );
}

function DiscardDialog({ project, round, onClose }: { project: Project; round: Round; onClose: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [why, setWhy] = useState('');
  const [instructions, setInstructions] = useState('');
  const discard = useMutation({
    mutationFn: (redo: boolean) => post<{ archived: string; ideas_reopened: number; ideas_moved: number; comments: number; run: unknown; redo_error?: string }>(
      `/api/projects/${project.id}/rounds/${round.number}/discard`, { why, redo, instructions }),
    onSuccess: (r) => {
      for (const k of ['rounds', 'round', 'ideas', 'next-round', 'spec-handoff']) qc.invalidateQueries({ queryKey: [k, project.id] });
      toast(`${round.label} moved to ${r.archived}.` + (r.redo_error ? ` Couldn't start the redo: ${r.redo_error}` : r.run ? ' Claude is making it again.' : ''), !!r.redo_error);
      onClose();
      navigate(`/projects/${project.id}`);
    },
    onError: (e: Error) => toast(e.message, true),
  });
  const prev = round.number > 1 ? `v${round.number - 1}` : null;
  return (
    <Modal title={`Discard ${round.label}?`} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); discard.mutate(false); }}>
        <ul className="small" style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
          <li>Its files (designs, notes, review, PDF) move to <code>archive/</code>, with its comments and the decisions it added. Nothing is deleted.</li>
          <li>The ideas it applied wait to be sent again{prev ? <>, and ideas noted on {round.label} move to {prev}</> : null}. The next round is {round.label} again.</li>
          {round.shared_at && <li style={{ color: 'var(--bad)' }}><b>{project.client.name} has seen it</b> (shared {ago(round.shared_at)}). It disappears for them, and their comments on it move to the archive.</li>}
        </ul>
        <label className="field"><span>What went wrong</span>
          <textarea className="input" rows={3} value={why} onChange={(e) => setWhy(e.target.value)} maxLength={2000} required
                    placeholder="e.g. It dropped the brand colours and redid screens nobody asked about" />
        </label>
        {can('claude.run') && <label className="field"><span>Instructions for the redo (optional)</span>
          <textarea className="input" rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={4000}
                    placeholder="e.g. Change only the screens the ideas name" />
        </label>}
        <p className="muted tiny" style={{ margin: 0 }}>Discard and redo sends the same ideas to Claude straight away, with what went wrong and your instructions.</p>
        <div className="row wrap" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn danger" disabled={!why.trim() || discard.isPending}>Discard</button>
          {can('claude.run') && <button type="button" className="btn primary" disabled={!why.trim() || discard.isPending} onClick={() => discard.mutate(true)}>
            {discard.isPending && <span className="spin" />}Discard and redo</button>}
        </div>
      </form>
    </Modal>
  );
}

// ---------- the list on a project page ----------

export function RoundsSection({ project }: { project: Project }) {
  const { me, can } = useAuth();
  useDesignTopics();
  const { data: rounds = [], isLoading } = useQuery({ queryKey: ['rounds', project.id], queryFn: () => get<Round[]>(`/api/projects/${project.id}/rounds`) });
  const [all, setAll] = useState(false);
  const shown = all ? rounds : rounds.slice(0, 4);
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-head"><h2>Designs</h2>{me?.is_staff && <span className="muted tiny mono">{project.path}/designs</span>}</div>
      {me?.is_staff && <ProjectKitBar project={project} canEdit={can('delivery.edit')} />}
      {shown.map((r) => (
        <div key={r.number} className="round-row">
          <Link to={`/projects/${project.id}/designs/${r.number}`} className="round-label">{r.label}</Link>
          <div className="grow" style={{ minWidth: 160 }}>
            <div className="row wrap" style={{ gap: 6 }}>
              <VisibilityBadge v={r.visibility} />
              {r.open_comments > 0 && <span className="badge brand">{r.open_comments} open comment{r.open_comments === 1 ? '' : 's'}</span>}
              {r.kit && <Link to={r.kit.kit_id ? `/kits/${r.kit.kit_id}?v=${r.kit.version}` : '#'} className="badge tiny-badge" title="The UI kit this round uses">{r.kit.name} v{r.kit.version}</Link>}
              {r.claude_working && <span className="badge info"><span className="spin" style={{ width: 10, height: 10 }} /> Claude is making this round</span>}
              {me?.is_staff && !r.built && !r.claude_working && <span className="badge warn">Not built</span>}
              {me?.is_staff && r.built && r.stale && !r.shared_at && <span className="badge warn" title="Files changed since the last build">Out of date</span>}
            </div>
            <div className="muted tiny" style={{ marginTop: 4 }}>
              {r.built ? `${r.screen_count} screens` : 'No screens yet'}
              {r.built_at && ` · built ${ago(r.built_at)}`}
              {r.shared_at && ` · shared ${ago(r.shared_at)}`}
              {r.approved_at && ` · approved ${ago(r.approved_at)}`}
            </div>
          </div>
          {me?.is_staff ? <RoundControls project={project} round={r} compact /> : null}
          {r.built && <Link className="btn sm" to={`/projects/${project.id}/designs/${r.number}`}>Open</Link>}
        </div>
      ))}
      {rounds.length > shown.length && <button className="btn ghost sm block" style={{ borderRadius: 0, height: 40 }} onClick={() => setAll(true)}>Show {rounds.length - shown.length} older round{rounds.length - shown.length === 1 ? '' : 's'}</button>}
      {!isLoading && !rounds.length && <div className="empty">{me?.is_staff ? 'No design rounds yet. Start the first one from the brief above.' : 'Designs will appear here when Blue Market shares them with you.'}</div>}
    </section>
  );
}

// ---------- the round viewer ----------

/** Overview, screens, notes and comments for one round. Shared by the signed-in viewer and the review-link page. */
export function RoundView({ round, comments, commentsApi, screenUrl, pdfUrl, eyebrow, headLeft, headRight, ideasProject, ticketsBy }: {
  round: RoundDetail; comments: CommentList | undefined; commentsApi: CommentsApi; screenUrl: (screenId: string) => string;
  pdfUrl: string; eyebrow: string; headLeft: ReactNode; headRight?: ReactNode;
  ideasProject?: string;      // Blue Market: show the Ideas tab for this project
  ticketsBy?: Record<string, RoundTicket[]>;     // tickets linked to each screen ("" = the whole round)
}) {
  const [search, setSearch] = useSearchParams();
  const screenId = search.get('screen');
  const [notesOpen, setNotesOpen] = useState(() => { try { return localStorage.getItem('bmp.notes') !== 'off'; } catch { return true; } });
  const toggleNotes = () => { const v = !notesOpen; setNotesOpen(v); try { localStorage.setItem('bmp.notes', v ? 'on' : 'off'); } catch { /* private mode */ } };
  const [savedTab, setTabState] = useState<PanelTab>(() => { try { return (localStorage.getItem('bmp.panel') as PanelTab) || 'notes'; } catch { return 'notes'; } });
  const tab: PanelTab = savedTab === 'ideas' && !ideasProject ? 'notes' : savedTab;
  const setTab = (t: PanelTab) => { setTabState(t); try { localStorage.setItem('bmp.panel', t); } catch { /* private mode */ } };
  const [overviewTab, setOverviewTab] = useState<'comments' | 'ideas'>('comments');
  const { data: next } = useNextRound(ideasProject ?? '', !!ideasProject);
  const openIdeas = useMemo(() => {
    const by: Record<string, number> = {};
    for (const i of next?.ideas ?? []) {
      if (i.status !== 'open' && i.status !== 'submitted') continue;
      // Count each idea once per screen it shows on (its own place, and its comment's place on this round).
      const keys = new Set<string>();
      if (i.round === round.number) keys.add(i.screen_id ?? '');
      if (i.comment?.round === round.number) keys.add(i.comment.screen_id ?? '');
      for (const k of keys) by[k] = (by[k] ?? 0) + 1;
    }
    return by;
  }, [next, round.number]);
  const index = round.screens.findIndex((s) => s.id === screenId);
  const current = index >= 0 ? round.screens[index] : null;
  // Main screens are the numbered sequence; a main screen and its variants (tabs, states) form a group.
  const { mains, groups, mainNo } = useMemo(() => {
    const mains = round.screens.filter((s) => !s.parent);
    const groups: Record<string, Screen[]> = {};
    for (const s of round.screens) (groups[s.parent ?? s.id] ??= []).push(s);
    return { mains, groups, mainNo: Object.fromEntries(mains.map((s, i) => [s.id, i + 1])) as Record<string, number> };
  }, [round.screens]);
  const group = current ? groups[current.parent ?? current.id] : [];
  const main = current ? round.screens.find((s) => s.id === (current.parent ?? current.id)) ?? current : null;
  const groupCount = (id: string) => (groups[id] ?? []).reduce((n, s) => n + (round.open_comments_by_screen[s.id] ?? 0), 0);
  const go = useCallback((i: number | null) => {
    const next = new URLSearchParams(search);
    if (i === null || i < 0) next.delete('screen'); else next.set('screen', round.screens[Math.min(i, round.screens.length - 1)].id);
    setSearch(next, { replace: true });
  }, [round, search, setSearch]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, select')) return;
      if (e.key === 'ArrowRight' || e.key === 'j') go(index + 1);
      if (e.key === 'ArrowLeft' || e.key === 'k') go(index - 1 < 0 ? null : index - 1);
      if (e.key === 'Escape') go(null);
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [go, index]);

  const { ref: stageRef, scale } = useFitScale(current?.frame, current?.frame === 'desktop' ? 1 : 0.9, group.length > 1 ? 48 : 0);
  // Keep the current screen visible in the list (it scrolls sideways on phones). Only the list scrolls, never the page.
  const listRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = listRef.current;
    const btn = nav?.querySelector<HTMLElement>('button.active');
    if (!nav || !btn) return;
    if (nav.scrollWidth > nav.clientWidth) nav.scrollLeft = btn.offsetLeft - (nav.clientWidth - btn.offsetWidth) / 2;
    else if (btn.offsetTop < nav.scrollTop || btn.offsetTop + btn.offsetHeight > nav.scrollTop + nav.clientHeight) nav.scrollTop = btn.offsetTop - nav.clientHeight / 3;
  }, [index]);

  return (
    <div className="viewer">
      <div className="viewer-head">
        <div className="row wrap" style={{ gap: 10, minWidth: 0 }}>
          {headLeft}
          <VisibilityBadge v={round.visibility} />
          {round.built_at && <span className="muted tiny">Built {ago(round.built_at)}</span>}
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          {headRight}
          <button className={`btn sm ${notesOpen ? 'primary' : ''}`} onClick={toggleNotes} aria-pressed={notesOpen} title="Show or hide notes and comments">Panel</button>
          {round.pdf && <a className="btn sm" href={pdfUrl} target="_blank" rel="noopener">PDF</a>}
        </div>
      </div>
      {!round.built ? (
        <div className="page"><div className="card empty">This round hasn't been built yet.</div></div>
      ) : (
        <div className={`viewer-body ${!notesOpen ? 'no-notes' : ''}`}>
          <nav className="screen-list" aria-label="Screens" ref={listRef}>
            <button className={current ? '' : 'active'} onClick={() => go(null)}><span className="num">·</span><span className="grow">Overview</span>{!!round.open_comments_by_screen[''] && <span className="count" title="Open general comments">{round.open_comments_by_screen['']}</span>}</button>
            {mains.map((s) => {
              const open = main?.id === s.id;
              const count = open ? round.open_comments_by_screen[s.id] : groupCount(s.id);
              return [
                <button key={s.id} className={current?.id === s.id ? 'active' : ''} onClick={() => go(round.screens.indexOf(s))}>
                  <span className="num">{mainNo[s.id]}</span><span className="grow">{s.title}</span>
                  {s.theme_name && <span className="theme-tag" title="Theme">{s.theme_name}</span>}
                  {!open && groups[s.id].length > 1 && <span className="muted tiny" title="Other views of this screen">+{groups[s.id].length - 1}</span>}
                  {!!count && <span className="count" title="Open comments">{count}</span>}
                </button>,
                ...(open ? groups[s.id].slice(1).map((v) => (
                  <button key={v.id} className={`sub ${current?.id === v.id ? 'active' : ''}`} onClick={() => go(round.screens.indexOf(v))}>
                    <span className="num" /><span className="grow">{v.label || v.title}</span>
                    {v.theme !== s.theme && <span className="theme-tag" title="Theme">{v.theme_name ?? 'Base'}</span>}
                    {!!round.open_comments_by_screen[v.id] && <span className="count" title="Open comments">{round.open_comments_by_screen[v.id]}</span>}
                  </button>
                )) : []),
              ];
            })}
          </nav>
          {current ? (
            <>
              <div className={`stage ${current.frame} ${group.length > 1 ? 'has-views' : ''}`} ref={stageRef}>
                {group.length > 1 && (
                  <div className="views" role="tablist" aria-label={`Views of ${main?.title}`}>
                    {group.map((v) => (
                      <button key={v.id} role="tab" aria-selected={v.id === current.id} className={v.id === current.id ? 'active' : ''} onClick={() => go(round.screens.indexOf(v))}>
                        {v.label || (v.parent ? v.title : 'Main')}
                        {!!round.open_comments_by_screen[v.id] && <span className="count">{round.open_comments_by_screen[v.id]}</span>}
                      </button>
                    ))}
                  </div>
                )}
                <button className="stage-nav prev" onClick={() => go(index - 1 < 0 ? null : index - 1)} aria-label="Previous screen">‹</button>
                <Frame src={screenUrl(current.id)} frame={current.frame} scale={scale} title={current.title} />
                <button className="stage-nav next" onClick={() => go(index + 1)} disabled={index >= round.screens.length - 1} aria-label="Next screen">›</button>
              </div>
              {notesOpen && <aside className="notes-panel">
                <div className="eyebrow">Screen {mainNo[main?.id ?? current.id]} of {mains.length}{current.parent ? ` · ${current.label || 'view'}` : ''}{current.theme_name ? ` · ${current.theme_name} theme` : ''}</div>
                <h2 style={{ margin: '4px 0 10px' }}>{current.title}</h2>
                <TicketChips tickets={ticketsBy?.[current.id]} />
                <div className="panel-tabs" role="tablist">
                  <button role="tab" aria-selected={tab === 'notes'} className={tab === 'notes' ? 'active' : ''} onClick={() => setTab('notes')}>Notes</button>
                  <button role="tab" aria-selected={tab === 'comments'} className={tab === 'comments' ? 'active' : ''} onClick={() => setTab('comments')}>
                    Comments{!!round.open_comments_by_screen[current.id] && <span className="count">{round.open_comments_by_screen[current.id]}</span>}
                  </button>
                  {ideasProject && <button role="tab" aria-selected={tab === 'ideas'} className={tab === 'ideas' ? 'active' : ''} onClick={() => setTab('ideas')}>
                    Ideas{!!openIdeas[current.id] && <span className="count">{openIdeas[current.id]}</span>}
                  </button>}
                </div>
                {tab === 'notes'
                  ? (current.notes_html ? <div className="prose" dangerouslySetInnerHTML={{ __html: current.notes_html }} />
                    : current.parent && main?.notes_html ? <><p className="muted small" style={{ marginTop: 0 }}>Another view of <b>{main.title}</b>. Its notes:</p><div className="prose" dangerouslySetInnerHTML={{ __html: main.notes_html }} /></>
                    : <p className="muted small">No notes for this screen.</p>)
                  : tab === 'ideas' && ideasProject
                    ? <IdeasPanel projectId={ideasProject} round={round.number} screenId={current.id} screenTitle={current.title} />
                    : <CommentsPanel api={commentsApi} round={round} screenId={current.id} screenTitle={current.title} list={comments} />}
              </aside>}
            </>
          ) : (
            <div className="overview">
              <div className="overview-inner">
                <div className="eyebrow">{eyebrow}</div>
                {round.intro_html && <div className="prose intro" dangerouslySetInnerHTML={{ __html: round.intro_html }} />}
                <div className="thumbs">
                  {mains.map((s) => (
                    <button key={s.id} className={`thumb ${s.frame}`} onClick={() => go(round.screens.indexOf(s))}>
                      <Frame src={screenUrl(s.id)} frame={s.frame} scale={s.frame === 'desktop' ? 0.2 : 0.36} title={s.title} lazy />
                      <span className="small"><b>{mainNo[s.id]}.</b> {s.title}{groups[s.id].length > 1 && <span className="muted"> · {groups[s.id].length} views</span>}{s.theme_name && <span className="theme-tag">{s.theme_name}</span>}</span>
                    </button>
                  ))}
                </div>
                {round.closing.map((c) => (
                  <section key={c.heading} className="prose closing"><h2>{c.heading}</h2><div dangerouslySetInnerHTML={{ __html: c.html }} /></section>
                ))}
              </div>
            </div>
          )}
          {!current && notesOpen && (
            <aside className="notes-panel">
              <div className="eyebrow">Round v{round.number}</div>
              <TicketChips tickets={ticketsBy?.['']} />
              {ideasProject ? (
                <div className="panel-tabs" role="tablist" style={{ marginTop: 6 }}>
                  <button role="tab" aria-selected={overviewTab === 'comments'} className={overviewTab === 'comments' ? 'active' : ''} onClick={() => setOverviewTab('comments')}>
                    General comments{!!round.open_comments_by_screen[''] && <span className="count">{round.open_comments_by_screen['']}</span>}
                  </button>
                  <button role="tab" aria-selected={overviewTab === 'ideas'} className={overviewTab === 'ideas' ? 'active' : ''} onClick={() => setOverviewTab('ideas')}>
                    Ideas{!!openIdeas[''] && <span className="count">{openIdeas['']}</span>}
                  </button>
                </div>
              ) : <h2 style={{ margin: '4px 0 12px' }}>General comments</h2>}
              {ideasProject && overviewTab === 'ideas'
                ? <IdeasPanel projectId={ideasProject} round={round.number} screenId={null} />
                : <CommentsPanel api={commentsApi} round={round} screenId={null} list={comments}
                                 onJump={(sid) => { setTab('comments'); go(round.screens.findIndex((s) => s.id === sid)); }} />}
            </aside>
          )}
        </div>
      )}
    </div>
  );
}

/** Tickets linked to a screen, as small chips that open the ticket. */
function TicketChips({ tickets }: { tickets?: RoundTicket[] }) {
  if (!tickets?.length) return null;
  return (
    <div className="ticket-chips">
      {tickets.map((t) => (
        <Link key={t.id} to={t.url} className={`ticket-chip cat-${t.category}`} title={`${t.title} · ${t.status}`}>
          <span className="num">#{t.number}</span><span className="ellipsis">{t.title}</span>
        </Link>
      ))}
    </div>
  );
}

export function RoundViewer() {
  const { id = '', n = '' } = useParams();
  const number = Number(n);
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { me } = useAuth();
  const setCrumbs = useCrumbs();
  useDesignTopics();
  const { data: project } = useQuery({ queryKey: ['project', id], queryFn: () => get<Project>(`/api/projects/${id}`) });
  const { data: rounds = [] } = useQuery({ queryKey: ['rounds', id], queryFn: () => get<Round[]>(`/api/projects/${id}/rounds`) });
  const { data: round, error } = useQuery({ queryKey: ['round', id, number], queryFn: () => get<RoundDetail>(`/api/projects/${id}/rounds/${number}`), retry: false });
  const commentsApi = useMemo(() => roundCommentsApi(id, number), [id, number]);
  const { data: comments } = useComments(commentsApi);
  const { data: roundTickets } = useQuery({ queryKey: ['round-tickets', id, number], queryFn: () => get<{ by_screen: Record<string, RoundTicket[]> }>(`/api/projects/${id}/rounds/${number}/tickets`), retry: false });
  const screenId = search.get('screen');

  useEffect(() => {
    if (!project) return;
    const base = me?.is_staff
      ? [{ label: 'Clients', to: '/clients' }, { label: project.client.name, to: `/clients/${project.client.id}?tab=projects` }]
      : [{ label: project.client.name, to: '/' }];
    setCrumbs([...base, { label: project.name, to: `/projects/${project.id}` }, { label: `Designs v${number}` }]);
  }, [project, me, number, setCrumbs]);

  if (error) return <Page title="Not found"><p className="muted">This round doesn't exist, or it hasn't been shared with you.</p></Page>;
  if (!round || !project) return <main className="page"><span className="spin muted" /></main>;

  return (
    <RoundView round={round} comments={comments} commentsApi={commentsApi} eyebrow={`${project.name} · v${number}`}
               screenUrl={(sid) => screenUrl(id, number, sid)} pdfUrl={`/files/projects/${id}/v${number}/pdf`}
               headLeft={
                 <select className="input" style={{ width: 'auto', minHeight: 32, padding: '4px 8px', fontWeight: 600 }} value={number}
                         onChange={(e) => navigate(`/projects/${id}/designs/${e.target.value}${screenId ? `?screen=${encodeURIComponent(screenId)}` : ''}`)} aria-label="Round">
                   {rounds.map((r) => <option key={r.number} value={r.number}>{r.label}{me?.is_staff ? ` · ${VISIBILITY_LABELS[r.visibility]}` : ''}</option>)}
                 </select>
               }
               headRight={me?.is_staff && <RoundControls project={project} round={round} compact />}
               ideasProject={me?.is_staff ? project.id : undefined} ticketsBy={roundTickets?.by_screen} />
  );
}
